import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ArticleCard } from "@/components/ArticleCard";
import AdSlot from "@/components/AdSlot";
import Breadcrumb from "@/components/Breadcrumb";
import SEO from "@/components/SEO";
import ArticleDisclaimer from "@/components/ArticleDisclaimer";
import { EDITORIAL_ORG } from "@/config/authors";
import { format } from "date-fns";
import { Clock, Share, TwitterLogo, LinkedinLogo, Link as LinkIcon, Sparkle } from "@phosphor-icons/react";
import { toast } from "sonner";

const fmt = (iso) => {
  try { return format(new Date(iso), "MMMM d, yyyy"); } catch { return ""; }
};

function Section({ id, title, children }) {
  return (
    <section id={id} className="mt-12 scroll-mt-24">
      <h2 className="font-display text-3xl font-black border-b-2 border-foreground pb-2 mb-6 tracking-tight">
        {title}
      </h2>
      {children}
    </section>
  );
}

function BulletList({ items, ordered = false }) {
  const Tag = ordered ? "ol" : "ul";
  return (
    <Tag className={`space-y-3 ${ordered ? "list-decimal" : "list-disc"} pl-6 marker:text-primary marker:font-bold`}>
      {items.map((item, i) => (
        <li key={i} className="text-[1.05rem] leading-relaxed text-foreground/90">
          {item}
        </li>
      ))}
    </Tag>
  );
}

export default function ArticlePage() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [related, setRelated] = useState([]);

  useEffect(() => {
    setArticle(null);
    setRelated([]);
    window.scrollTo(0, 0);
    api.article(slug).then(setArticle).catch(() => setArticle(false));
    api.related(slug, 3).then(setRelated).catch(() => {});
  }, [slug]);

  if (article === false) {
    return <div className="max-w-3xl mx-auto py-24 text-center">Article not found.</div>;
  }
  if (!article) {
    return <div className="max-w-3xl mx-auto py-24 text-center text-foreground/60">Loading article…</div>;
  }

  const url = window.location.href;
  const share = {
    twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(article.title)}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
  };

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": article.title,
    "description": article.meta_description,
    "image": [article.cover_image],
    "author": {
      "@type": "Organization",
      "name": EDITORIAL_ORG.name,
      "url": (process.env.REACT_APP_SITE_URL || "") + "/editorial",
      "description": EDITORIAL_ORG.short_bio,
      ...(EDITORIAL_ORG.linkedin ? { "sameAs": [EDITORIAL_ORG.linkedin, EDITORIAL_ORG.twitter].filter(Boolean) } : {}),
    },
    "publisher": {
      "@type": "Organization",
      "name": "Finance Topia",
      "logo": {
        "@type": "ImageObject",
        "url": (process.env.REACT_APP_SITE_URL || "") + "/logo192.png",
      },
    },
    "datePublished": article.published_at,
    "dateModified": article.updated_at,
    "keywords": article.meta_keywords,
    "articleSection": article.category_name,
    "mainEntityOfPage": { "@type": "WebPage", "@id": url },
    "wordCount": [
      article.introduction,
      article.conclusion,
      ...(article.benefits || []),
      ...(article.risks || []),
      ...(article.step_by_step || []),
    ].join(" ").split(/\s+/).length,
    // AI transparency: Google supports OpenGraph/schema tags for
    // machine-generated content. Explicit disclosure improves E-E-A-T
    // and reduces "scaled content abuse" flag risk under 2024 policy.
    "creativeWorkStatus": "Published",
    "isBasedOn": "AI-assisted editorial process, human-reviewed",
  };

  const faqLd = article.faqs?.length ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": article.faqs.map((f) => ({
      "@type": "Question",
      "name": f.question,
      "acceptedAnswer": { "@type": "Answer", "text": f.answer },
    })),
  } : null;

  const toc = [
    { id: "introduction", label: "Introduction" },
    article.benefits?.length && { id: "benefits", label: "Benefits" },
    article.risks?.length && { id: "risks", label: "Risks" },
    article.examples?.length && { id: "examples", label: "Examples" },
    article.step_by_step?.length && { id: "steps", label: "Step-by-step" },
    article.common_mistakes?.length && { id: "mistakes", label: "Common Mistakes" },
    article.expert_tips?.length && { id: "tips", label: "Expert Tips" },
    article.faqs?.length && { id: "faqs", label: "FAQs" },
    { id: "conclusion", label: "Conclusion" },
  ].filter(Boolean);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied");
    } catch { toast.error("Copy failed"); }
  };

  return (
    <>
      <SEO
        title={article.title}
        description={article.meta_description}
        keywords={article.meta_keywords}
        image={article.cover_image}
        url={`/article/${article.slug}`}
        type="article"
        publishedTime={article.published_at}
        jsonLd={jsonLd}
      />
      {faqLd && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(faqLd)
              .replace(/</g, "\\u003c")
              .replace(/>/g, "\\u003e")
              .replace(/&/g, "\\u0026"),
          }}
        />
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Breadcrumb items={[
          { label: "Home", to: "/" },
          { label: article.category_name, to: `/category/${article.category_slug}` },
          { label: article.title.length > 40 ? article.title.slice(0, 40) + "…" : article.title },
        ]} />
      </div>

      <article className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid gap-10 lg:grid-cols-12 pb-16">
        {/* Left sticky TOC */}
        <aside className="hidden lg:block lg:col-span-3">
          <div className="sticky top-24">
            <div className="overline mb-4">Contents</div>
            <ul className="space-y-2 text-sm">
              {toc.map((t) => (
                <li key={t.id}>
                  <a
                    href={`#${t.id}`}
                    data-testid={`toc-${t.id}`}
                    className="text-foreground/70 hover:text-primary transition-colors duration-200"
                  >
                    {t.label}
                  </a>
                </li>
              ))}
            </ul>
            <div className="mt-8 border-t border-foreground/15 pt-6">
              <div className="overline mb-3">Share</div>
              <div className="flex gap-2">
                <a href={share.twitter} target="_blank" rel="noreferrer" data-testid="share-twitter" className="border border-foreground/20 p-2 hover:bg-foreground hover:text-background transition-colors duration-200"><TwitterLogo size={16} /></a>
                <a href={share.linkedin} target="_blank" rel="noreferrer" data-testid="share-linkedin" className="border border-foreground/20 p-2 hover:bg-foreground hover:text-background transition-colors duration-200"><LinkedinLogo size={16} /></a>
                <button onClick={copyLink} data-testid="share-copy" className="border border-foreground/20 p-2 hover:bg-foreground hover:text-background transition-colors duration-200"><LinkIcon size={16} /></button>
              </div>
            </div>
          </div>
        </aside>

        {/* Center article body */}
        <div className="lg:col-span-6">
          <div className="overline text-primary" data-testid="article-category">{article.category_name}</div>
          <h1 data-testid="article-title" className="font-display font-black text-4xl sm:text-5xl leading-[1.05] tracking-tight mt-3">
            {article.title}
          </h1>
          <p className="mt-4 text-lg text-foreground/70 leading-relaxed">
            {article.subtitle}
          </p>
          <div className="mt-6 flex items-center flex-wrap gap-x-4 gap-y-2 text-xs font-mono text-foreground/60 border-y border-foreground/15 py-4">
            <span>By <Link to="/editorial" className="text-foreground font-semibold hover:text-primary transition-colors duration-200">{EDITORIAL_ORG.name}</Link></span>
            <span>·</span>
            <span>{fmt(article.published_at)}</span>
            <span>·</span>
            <span className="inline-flex items-center gap-1"><Clock size={12} />{article.reading_time} min read</span>
            <span>·</span>
            <span
              data-testid="ai-assisted-tag"
              title="This article was drafted with AI assistance and reviewed by our editorial team."
              className="inline-flex items-center gap-1 border border-foreground/20 px-2 py-0.5 uppercase tracking-wider text-[10px]"
            >
              <Sparkle size={10} weight="bold" /> AI-assisted research
            </span>
          </div>

          <figure className="mt-8">
            <img
              src={article.cover_image}
              alt={article.title}
              className="w-full aspect-[16/9] object-cover border border-foreground/10"
            />
          </figure>

          {/* Introduction */}
          <section id="introduction" className="mt-10 scroll-mt-24">
            <div className="prose-article">
              {article.introduction.split("\n\n").map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
          </section>

          <AdSlot size="inline" />

          {article.benefits?.length > 0 && (
            <Section id="benefits" title="Benefits">
              <BulletList items={article.benefits} />
            </Section>
          )}

          {article.risks?.length > 0 && (
            <Section id="risks" title="Risks to Understand">
              <BulletList items={article.risks} />
            </Section>
          )}

          {article.examples?.length > 0 && (
            <Section id="examples" title="Real-World Examples">
              <BulletList items={article.examples} />
            </Section>
          )}

          <AdSlot size="inline" />

          {article.step_by_step?.length > 0 && (
            <Section id="steps" title="Step-by-Step Guide">
              <BulletList items={article.step_by_step} ordered />
            </Section>
          )}

          {article.common_mistakes?.length > 0 && (
            <Section id="mistakes" title="Common Mistakes to Avoid">
              <BulletList items={article.common_mistakes} />
            </Section>
          )}

          {article.expert_tips?.length > 0 && (
            <Section id="tips" title="Expert Tips">
              <BulletList items={article.expert_tips} />
            </Section>
          )}

          {article.faqs?.length > 0 && (
            <Section id="faqs" title="Frequently Asked Questions">
              <div className="divide-y divide-foreground/10 border border-foreground/15">
                {article.faqs.map((f, i) => (
                  <details key={i} data-testid={`faq-${i}`} className="group p-5 open:bg-muted/40">
                    <summary className="cursor-pointer font-display font-bold text-lg list-none flex items-start justify-between gap-4">
                      <span>{f.question}</span>
                      <span className="text-primary text-2xl leading-none group-open:rotate-45 transition-transform duration-200">+</span>
                    </summary>
                    <p className="mt-3 text-foreground/80 leading-relaxed">{f.answer}</p>
                  </details>
                ))}
              </div>
            </Section>
          )}

          <Section id="conclusion" title="Conclusion">
            <div className="prose-article">
              {article.conclusion.split("\n\n").map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
          </Section>

          <ArticleDisclaimer />

          <div className="mt-12 border-t border-foreground/15 pt-6 flex flex-wrap gap-2">
            {article.tags?.map((t) => (
              <Link
                key={t}
                to={`/search?q=${encodeURIComponent(t)}`}
                className="text-xs font-mono uppercase tracking-wider border border-foreground/20 px-3 py-1 hover:bg-foreground hover:text-background transition-colors duration-200"
              >
                {t}
              </Link>
            ))}
          </div>
        </div>

        {/* Right sidebar */}
        <aside className="lg:col-span-3">
          <div className="sticky top-24 space-y-8">
            <AdSlot size="square" />
            <div>
              <div className="overline mb-3">Related</div>
              <div className="space-y-4">
                {related.map((r) => (
                  <Link key={r.id} to={`/article/${r.slug}`} data-testid={`related-${r.slug}`} className="block group">
                    <img src={r.cover_image} alt={r.title} className="w-full aspect-[16/10] object-cover" />
                    <h4 className="font-display font-bold mt-2 leading-snug group-hover:text-primary transition-colors duration-200">
                      {r.title}
                    </h4>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </aside>
      </article>

      {/* Mobile share */}
      <div className="lg:hidden flex items-center justify-center gap-3 pb-8">
        <span className="overline">Share</span>
        <a href={share.twitter} target="_blank" rel="noreferrer" className="border border-foreground/20 p-2"><TwitterLogo size={16} /></a>
        <a href={share.linkedin} target="_blank" rel="noreferrer" className="border border-foreground/20 p-2"><LinkedinLogo size={16} /></a>
        <button onClick={copyLink} className="border border-foreground/20 p-2"><Share size={16} /></button>
      </div>
    </>
  );
}
